import { createDrawingHistory, historyReducer } from "./history";
import { drawingReducer } from "./stateMachine";
import { clonePoint } from "./shared";
/**
 * Every `DrawingHistoryAction` type, written as a RECORD rather than a list of
 * string comparisons.
 *
 * The previous form was `action.type === "PUSH" || action.type === "REPLACE" || …`,
 * which TypeScript cannot check. It therefore drifted silently: when `RECONCILE`
 * was added in P3-07 it was never added here, and the action fell through to
 * `drawingReducer`, where it was a no-op — a new history action that appeared to
 * work in the reducer tests and did nothing in the hook. `interactionState.ts`
 * carried a near-duplicate of this predicate missing THREE actions — `RECONCILE`,
 * `RESET_SCOPE`, `REORDER` — which is the same drift already visible in the tree,
 * one place further along it. That duplicate was deleted on 2026-10-12; the note
 * that used to sit here named only two of the three, which is why nobody counted
 * them.
 *
 * `RESET_SCOPE` is the one to watch: it discards the timeline, so a copy that
 * drops it would keep undo/redo wired against a history that undo may no longer
 * move.
 *
 * A `Record<DrawingHistoryAction["type"], true>` makes an omission a COMPILE
 * error: add an action to the union and this map fails to type-check until the
 * predicate knows about it. That is the whole point — the failure moves from
 * "a runtime no-op nobody notices" to "the build breaks".
 */
const HISTORY_ACTION_TYPES = {
    PUSH: true,
    REPLACE: true,
    RECONCILE: true,
    RESET_SCOPE: true,
    REORDER: true,
    UNDO: true,
    REDO: true,
    CLEAR: true,
};
export function isHistoryAction(action) {
    return Object.prototype.hasOwnProperty.call(HISTORY_ACTION_TYPES, action.type);
}
export function getSelectedObjectIds(state) {
    switch (state.type) {
        case "selected":
            return [state.objectId];
        case "selectedMultiple":
            return state.objectIds;
        case "moving":
        case "resizing":
        case "editing":
            return [state.objectId];
        default:
            return [];
    }
}
function cloneDrawingPoints(points) {
    return points.map(clonePoint);
}
function cloneSegmentStyles(segmentStyles) {
    return segmentStyles
        ? Object.fromEntries(Object.entries(segmentStyles).map(([key, value]) => [key, { ...value }]))
        : undefined;
}
export function patchDrawing(drawing, patch) {
    // P6.4 D-CORE-15: sanitize - never allow patch to overwrite id/createdAt
    const { id: _id, createdAt: _createdAt, ...safePatch } = patch;
    const p = safePatch;
    return {
        ...drawing,
        ...p,
        points: p.points ? cloneDrawingPoints(p.points) : cloneDrawingPoints(drawing.points),
        style: p.style ? { ...drawing.style, ...p.style } : { ...drawing.style },
        fibLevels: p.fibLevels ? [...p.fibLevels] : drawing.fibLevels ? [...drawing.fibLevels] : undefined,
        riskReward: p.riskReward ? { ...p.riskReward } : drawing.riskReward ? { ...drawing.riskReward } : undefined,
        fibLevelConfigs: p.fibLevelConfigs ? p.fibLevelConfigs.map((level) => ({ ...level })) : drawing.fibLevelConfigs ? drawing.fibLevelConfigs.map((level) => ({ ...level })) : undefined,
        segmentStyles: p.segmentStyles ? cloneSegmentStyles(p.segmentStyles) : cloneSegmentStyles(drawing.segmentStyles),
        segmentVisible: p.segmentVisible ? { ...p.segmentVisible } : drawing.segmentVisible ? { ...drawing.segmentVisible } : undefined,
        updatedAt: Date.now(),
    };
}
export function replaceDrawingById(drawings, objectId, patch) {
    let didChange = false;
    const nextDrawings = drawings.map((drawing) => {
        if (drawing.id !== objectId) {
            return drawing;
        }
        didChange = true;
        return patchDrawing(drawing, patch);
    });
    return didChange ? nextDrawings : null;
}
export function createDrawingInteractionState(initialDrawings = []) {
    return {
        drawingState: { type: "idle" },
        history: createDrawingHistory(initialDrawings),
    };
}
export function drawingInteractionReducer(state, action) {
    if (isHistoryAction(action)) {
        return {
            ...state,
            history: historyReducer(state.history, action),
        };
    }
    return {
        ...state,
        drawingState: drawingReducer(state.drawingState, action),
    };
}
export function deleteSelectedInteractionState(state) {
    const selectedObjectIds = getSelectedObjectIds(state.drawingState);
    if (selectedObjectIds.length === 0) {
        return state;
    }
    const selectedDrawings = state.history.present.filter((drawing) => selectedObjectIds.includes(drawing.id));
    if (selectedDrawings.some((drawing) => drawing.locked)) {
        return state;
    }
    const nextDrawings = state.history.present.filter((drawing) => !selectedObjectIds.includes(drawing.id));
    const nextHistory = nextDrawings.length === state.history.present.length
        ? state.history
        : historyReducer(state.history, { type: "REPLACE", drawings: nextDrawings });
    return {
        drawingState: { type: "idle" },
        history: nextHistory,
    };
}
